// 🔧 car_show_playbook.js
// ✅ Purpose: Public-facing event playbook for running a car show with Let's Go Vote
// ✅ Features:
// - Explains how car shows can use categories, entries, QR codes, voting, sponsors, and winners
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Helps prospective customers understand the full car show workflow
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function CarShowPlaybook() {
  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Car Show Playbook | Let's Go Vote"
        description="Learn how to run a car show with People's Choice voting, QR codes, categories, entries, sponsors, winners, and event-day results using Let's Go Vote."
        path="/guides/car-show-playbook"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
          {
            name: "Car Show Playbook",
            path: "/guides/car-show-playbook",
          },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🚘 Car Show Playbook</h1>
          <p className="lead text-muted">
            Learn how to run a car show with categories, entries, QR codes,
            public voting, sponsors, and winners.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">Built for car show voting</h5>
            <p className="mb-0">
              Let&apos;s Go Vote helps car show organizers replace paper ballots
              with QR code voting, real-time entry lists, sponsor visibility,
              vote tracking, leaderboards, and winner displays.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 1: Create your car show event</h3>
          <p>
            Start by setting up the event name, dates, public event description,
            event logo, and event images. This becomes the public event home
            page attendees can visit before and during the show.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 2: Choose your voting style</h3>
          <p>
            Most car shows use People&apos;s Choice Voting, where attendees vote
            for their favorite vehicle in each category. Larger or more formal
            events may also use judged scoring or sponsor awards.
          </p>

          <Link
            to="/guides/configure-voting"
            className="btn btn-sm btn-outline-primary"
          >
            Read the Voting Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 3: Build car show categories</h3>
          <p>
            Categories help organize entries and awards. Example categories may
            include Stock, Custom, Convertible, Classic, Import, Truck, Best
            Interior, Best Paint, or Best in Show.
          </p>

          <div className="alert alert-secondary text-start">
            <strong>Tip:</strong> Keep categories simple enough for attendees to
            understand quickly while walking the show.
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 4: Add entries and registration numbers</h3>
          <p>
            Each vehicle can have an entry profile with a registration number,
            owner name, vehicle description, photos, and category assignment.
            Public entry submissions can also be enabled if you want owners to
            submit their own vehicle information before the event.
          </p>

          <Link
            to="/guides/categories-and-entries"
            className="btn btn-sm btn-outline-primary"
          >
            Read Categories & Entries
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 5: Print QR codes for vehicles</h3>
          <p>
            QR codes can be printed and placed near each vehicle. Attendees scan
            the code, view the entry, and submit their vote from their phone.
            This helps reduce paper ballots, counting errors, and event-day
            confusion.
          </p>

          <Link to="/guides/qr-codes" className="btn btn-sm btn-outline-primary">
            Read the QR Code Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 6: Add sponsors and sponsor awards</h3>
          <p>
            Sponsors can be displayed on the event page and sponsors page.
            Sponsor awards can also recognize special winners such as Sponsor
            Choice, Best Display, Best Paint, or other custom awards.
          </p>

          <Link to="/guides/sponsors" className="btn btn-sm btn-outline-primary">
            Read the Sponsors Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 7: Run voting on event day</h3>
          <p>
            During the show, attendees scan QR codes, vote for entries, and can
            view their ballot. Managers can monitor activity and publish winners
            when results are ready.
          </p>

          <Link
            to="/guides/running-event-day"
            className="btn btn-sm btn-outline-primary"
          >
            Read Running Event Day
          </Link>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Planning a car show?</h5>
            <p className="mb-3">
              Let&apos;s Go Vote can help your event manage QR code voting,
              entries, categories, sponsors, leaderboards, and winners.
            </p>

            <Link to="/request_event" className="btn btn-primary">
              Request an Event
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default CarShowPlaybook;